import type { TimelineEntry } from '@kizen/api';
import { useTranslation } from 'react-i18next';
import { TextSpan } from '__app/typography';

type Event = SnakeToCamelCase<TimelineEntry<Record<string, any>>>;

type Initiator = Event['initiator'];

type InitiatorType = Exclude<Initiator['type'], null>;

type Config = {
  prefix?: string;
  postfix?: string | ((event: Event, t: (x: string) => string) => string);
  creationType(
    initiator: Initiator,
    event: Event,
    t: (x: string) => string
  ): string;
};

export interface CardHeaderConfig
  extends Partial<Record<InitiatorType, Config>> {
  'empty-initiator'?: Partial<Config>;
}

type CardHeaderProps = {
  config(t: (x: string) => string, event: Event): CardHeaderConfig;
  event: Event;
  name: string;
  nameWeight?: 'black' | 'bold' | 'light' | 'regular';
};

export const CardHeader = (props: CardHeaderProps) => {
  const { event, name, nameWeight = 'bold', config: getConfig } = props;

  const { t } = useTranslation();

  const config = getConfig(t, event);
  const initiatorType = event.initiator?.type;
  const entry = initiatorType
    ? config[initiatorType]
    : config['empty-initiator'];

  let prefix = '';
  let initiator = '';
  let postfix = '';

  if (entry) {
    if (entry.creationType) {
      initiator = entry.creationType(event.initiator, event, t);
    }

    if (entry.prefix) {
      prefix = entry.prefix;
    }

    if (entry.postfix) {
      postfix =
        typeof entry.postfix === 'function'
          ? entry.postfix(event, t)
          : entry.postfix;
    }
  }

  return (
    <>
      <TextSpan weight={nameWeight} data-qa="field-name" size="micro">
        {name}
      </TextSpan>
      {prefix ? <TextSpan size="micro">&nbsp;{prefix}&nbsp;</TextSpan> : null}
      {initiator ? (
        <TextSpan weight="bold" size="micro">
          {initiator}
        </TextSpan>
      ) : null}
      {postfix ? <TextSpan size="micro">&nbsp;{postfix}</TextSpan> : null}
    </>
  );
};
